// Session 38 · Admin → Inventory → Venues table
// Full CRUD dashboard for platform admins to manage every venue on Métier.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";

function StatusBadge({ published, status }) {
  const state = status === "archived" ? "archived"
              : published ? "published" : "unpublished";
  const style = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "3px 10px", fontSize: 11, letterSpacing: "0.14em",
    textTransform: "uppercase", borderRadius: 999,
    background: state === "published" ? "rgba(20,120,60,0.12)"
              : state === "archived"  ? "rgba(80,80,80,0.14)"
              : "rgba(200,120,20,0.14)",
    color: state === "published" ? "#146F3B"
         : state === "archived"  ? "#4b4b4b"
         : "#8A5A00",
    border: state === "published" ? "1px solid rgba(20,120,60,0.18)"
          : state === "archived"  ? "1px solid rgba(80,80,80,0.20)"
          : "1px solid rgba(200,120,20,0.24)",
  };
  return <span style={style} data-testid={`venue-status-${state}`}>{state}</span>;
}

function GeocodeBadge({ status }) {
  if (!status) return <span style={{ color: "#999", fontSize: 12 }}>—</span>;
  const map = {
    ok: { label: "OK", color: "#146F3B", bg: "rgba(20,120,60,0.10)" },
    pending: { label: "PENDING", color: "#8A5A00", bg: "rgba(200,120,20,0.10)" },
    failed: { label: "FAILED", color: "#B03030", bg: "rgba(180,60,60,0.10)" },
    skipped: { label: "N/A", color: "#666", bg: "rgba(120,120,120,0.10)" },
  };
  const cfg = map[status] || map.pending;
  return (
    <span style={{ padding: "2px 8px", borderRadius: 5, fontSize: 10.5,
                    color: cfg.color, background: cfg.bg,
                    letterSpacing: "0.10em", textTransform: "uppercase" }}>
      {cfg.label}
    </span>
  );
}


export default function AdminInventoryVenues() {
  const nav = useNavigate();
  const [venues, setVenues] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [published, setPublished] = useState("");        // '' | 'true' | 'false'
  const [organizationId, setOrganizationId] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [openRow, setOpenRow] = useState(null);
  // Session 42 · Create-venue modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newVenue, setNewVenue] = useState({
    name: "", organization_id: "", city: "", state: "", postal_code: "",
    address_line_1: "", website: "", published: false,
  });

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (city.trim()) p.set("city", city.trim());
    if (state.trim()) p.set("state", state.trim());
    if (postalCode.trim()) p.set("postal_code", postalCode.trim());
    if (published) p.set("published", published);
    if (organizationId) p.set("organization_id", organizationId);
    p.set("limit", "200");
    return p.toString();
  }, [q, city, state, postalCode, published, organizationId]);

  // Session 59.5 · P0 · Stable callback so the effect can depend on it
  // without recreating on every render (react-hooks/exhaustive-deps).
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/venues?${params}`);
      setVenues(r.data.venues || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load venues");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Populate the organization filter dropdown from the current result set
    const map = new Map();
    for (const v of venues) {
      const o = v.organization;
      if (o && o.organization_id) map.set(o.organization_id, o.name || o.slug);
    }
    // Session 60.19 · Alphabetize the org list so admin scanning is predictable.
    const arr = Array.from(map.entries()).sort((a, b) =>
      (a[1] || "").localeCompare(b[1] || "")
    );
    setOrgs(arr);
  }, [venues]);

  const action = async (venue, act) => {
    const vid = venue.venue_id;
    try {
      if (act === "publish") await api.post(`/admin/venues/${vid}/publish`);
      else if (act === "unpublish") await api.post(`/admin/venues/${vid}/unpublish`);
      else if (act === "archive") {
        if (!window.confirm(`Archive "${venue.name}"? This also archives all its child workspaces.`)) return;
        await api.post(`/admin/venues/${vid}/archive`);
      }
      else if (act === "delete") {
        // Session 42 · Hard delete — final. Requires strict double confirmation
        // + a typed match on the venue name to prevent muscle-memory mistakes.
        const typed = window.prompt(
          `Delete "${venue.name}" permanently?\n\n` +
          `This removes the venue, all its child workspaces, related merchandising, ` +
          `and the parent organization (if empty). Financial history is preserved ` +
          `only for bookings that already completed.\n\n` +
          `Type the venue name to confirm:`
        );
        if (typed === null) return;                   // cancelled
        if (typed.trim() !== venue.name.trim()) {
          toast.error("Name didn't match — delete cancelled.");
          return;
        }
        await api.delete(`/admin/venues/${vid}`);
        toast.success(`"${venue.name}" deleted.`);
        await load();
        return;
      }
      else if (act === "retry-geocode") await api.post(`/admin/venues/${vid}/retry-geocode`);
      toast.success(`Venue ${act}ed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${act} failed`);
    }
  };

  const clearFilters = () => {
    setQ(""); setCity(""); setState(""); setPostalCode("");
    setPublished(""); setOrganizationId("");
  };

  return (
    <AppShell>
      <div className="m-container" style={{ paddingTop: 40, paddingBottom: 80 }}>
        <div style={{ marginBottom: 6, letterSpacing: "0.18em", fontSize: 11,
                       textTransform: "uppercase", color: "rgba(27,35,64,0.55)" }}>
          Admin · Inventory
        </div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
                       gap: 20, marginBottom: 24 }}>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 40, margin: 0,
                        color: "var(--m-navy)" }}>
            Venues
          </h1>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button type="button"
                    onClick={() => setCreateOpen(true)}
                    data-testid="admin-venues-new-btn"
                    className="m-btn"
                    style={{ fontSize: 13, padding: "8px 14px", background: "var(--gold)",
                             color: "#1B2340", border: "1px solid var(--gold)",
                             borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>
              + New venue
            </button>
            <Link to="/admin/inventory/workspaces" className="m-btn-ghost"
                   data-testid="admin-nav-workspaces"
                   style={{ fontSize: 13, padding: "8px 14px" }}>
              Workspaces →
            </Link>
            <span style={{ color: "rgba(27,35,64,0.55)", fontSize: 13 }}>
              {loading ? "Loading…" : `${total} venue${total === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>

        {createOpen && (
          <CreateVenueModal
            venue={newVenue}
            setVenue={setNewVenue}
            orgs={orgs}
            saving={creating}
            onClose={() => setCreateOpen(false)}
            onCreate={async () => {
              if (!newVenue.name?.trim()) {
                toast.error("Venue name is required");
                return;
              }
              setCreating(true);
              try {
                // Trim empty fields so the backend auto-creates org and skips
                // geocoding until real address components are supplied.
                const payload = Object.fromEntries(
                  Object.entries(newVenue).filter(([, v]) => v !== "" && v !== null),
                );
                const r = await api.post("/admin/venues", payload);
                toast.success(`Venue "${r.data.name}" created`);
                setCreateOpen(false);
                setNewVenue({ name: "", organization_id: "", city: "", state: "",
                              postal_code: "", address_line_1: "", website: "",
                              published: false });
                // Navigate to the new venue's Edit page so the admin can flesh
                // out photos, amenities, workspaces, etc. Bypasses list reload.
                nav(`/admin/venues/${r.data.venue_id}/edit`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Create failed");
              } finally {
                setCreating(false);
              }
            }}
          />
        )}

        {/* Filter row */}
        <div style={{ display: "grid",
                       gridTemplateColumns: "1.5fr 1fr 90px 100px 130px 150px auto",
                       gap: 10, alignItems: "end", marginBottom: 22,
                       padding: "16px 18px", background: "rgba(27,35,64,0.03)",
                       border: "1px solid rgba(27,35,64,0.08)", borderRadius: 10 }}>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>Search</span>
            <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="name, city, zip…"
                    data-testid="admin-venues-search"
                    style={{ padding: "8px 10px" }} />
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>City</span>
            <input value={city} onChange={e => setCity(e.target.value)}
                    data-testid="admin-venues-city" style={{ padding: "8px 10px" }} />
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>State</span>
            <select value={state} onChange={e => setState(e.target.value)}
                    data-testid="admin-venues-state" style={{ padding: "8px 10px" }}>
              <option value="">Any</option>
              {/* Session 60.19 · Alphabetized 2-letter US states + DC + territories. */}
              {[
                "AL","AK","AZ","AR","CA","CO","CT","DC","DE","FL","GA","HI","IA",
                "ID","IL","IN","KS","KY","LA","MA","MD","ME","MI","MN","MO","MS",
                "MT","NC","ND","NE","NH","NJ","NM","NV","NY","OH","OK","OR","PA",
                "PR","RI","SC","SD","TN","TX","UT","VA","VT","WA","WI","WV","WY",
              ].map(code => <option key={code} value={code}>{code}</option>)}
            </select>
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>ZIP</span>
            <input value={postalCode} onChange={e => setPostalCode(e.target.value)}
                    placeholder="03110"
                    data-testid="admin-venues-zip" style={{ padding: "8px 10px" }} />
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>Published</span>
            <select value={published} onChange={e => setPublished(e.target.value)}
                     data-testid="admin-venues-published"
                     style={{ padding: "8px 10px" }}>
              <option value="">Any</option>
              <option value="true">Published</option>
              <option value="false">Unpublished / drafts</option>
            </select>
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(27,35,64,0.55)" }}>Organization</span>
            <select value={organizationId} onChange={e => setOrganizationId(e.target.value)}
                     data-testid="admin-venues-org"
                     style={{ padding: "8px 10px" }}>
              <option value="">Any</option>
              {orgs.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
          <button type="button" onClick={clearFilters} className="m-btn-ghost"
                   style={{ padding: "9px 14px", fontSize: 12,
                            whiteSpace: "nowrap" }}
                   data-testid="admin-venues-clear-filters">
            Clear
          </button>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto", border: "1px solid rgba(27,35,64,0.10)",
                       borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
                  data-testid="admin-venues-table">
            <thead>
              <tr style={{ background: "rgba(27,35,64,0.04)",
                            borderBottom: "1px solid rgba(27,35,64,0.10)" }}>
                {["Venue", "Organization", "City", "State", "ZIP", "Workspaces",
                   "Published", "Booking", "Geo", "Updated", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 14px", textAlign: "left",
                                         fontWeight: 600, fontSize: 11,
                                         letterSpacing: "0.14em",
                                         textTransform: "uppercase",
                                         color: "rgba(27,35,64,0.62)" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {venues.length === 0 && !loading && (
                <tr>
                  <td colSpan={11} style={{ padding: "40px 20px", textAlign: "center",
                                              color: "rgba(27,35,64,0.55)" }}>
                    No venues match your filters.
                  </td>
                </tr>
              )}
              {/* Session 60.19 · Alphabetize venues by name for stable admin scanning. */}
              {[...venues].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(v => (
                <tr key={v.venue_id}
                     data-testid={`admin-venues-row-${v.venue_id}`}
                     style={{ borderTop: "1px solid rgba(27,35,64,0.06)" }}>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ fontWeight: 600, color: "var(--m-navy)" }}>
                      {/* Session 42 · Name is now a direct link to Edit (not to the public
                          page), matching the workspaces table UX. The public preview is
                          still reachable via Actions → "Open venue page". */}
                      <Link to={`/admin/venues/${v.venue_id}/edit`}
                             data-testid={`admin-venues-name-${v.venue_id}`}>{v.name}</Link>
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>
                      {v.venue_type || "—"} · {v.slug}
                    </div>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13 }}>
                    {v.organization?.name || <span style={{color:"#aaa"}}>—</span>}
                  </td>
                  <td style={{ padding: "12px 14px" }}>{v.city || "—"}</td>
                  <td style={{ padding: "12px 14px" }}>{v.normalized_state || v.state || "—"}</td>
                  <td style={{ padding: "12px 14px", fontFamily: "monospace" }}>
                    {v.postal_code || "—"}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center" }}
                       data-testid={`admin-venues-workspace-count-${v.venue_id}`}>
                    {v.workspace_count}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <StatusBadge published={v.published} status={v.status} />
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <span style={{ fontSize: 12,
                                    color: v.booking_status === "open"
                                          ? "#146F3B" : "rgba(27,35,64,0.6)" }}>
                      {v.booking_status || "open"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <GeocodeBadge status={v.geocode_status} />
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 12,
                                color: "rgba(27,35,64,0.55)" }}>
                    {v.updated_at ? new Date(v.updated_at).toLocaleDateString() : "—"}
                  </td>
                  <td style={{ padding: "8px 8px", position: "relative" }}>
                    <button
                      onClick={() => setOpenRow(openRow === v.venue_id ? null : v.venue_id)}
                      className="m-btn-ghost"
                      style={{ fontSize: 12, padding: "6px 10px" }}
                      data-testid={`admin-venues-actions-${v.venue_id}`}
                    >
                      Actions ▾
                    </button>
                    {openRow === v.venue_id && (
                      <div style={{ position: "absolute", top: "100%", right: 8, zIndex: 20,
                                     background: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
                                     border: "1px solid rgba(27,35,64,0.10)",
                                     borderRadius: 8, minWidth: 220, padding: "6px 0" }}
                           data-testid={`admin-venues-actions-menu-${v.venue_id}`}>
                        <ActionItem to={`/spaces/${v.slug}`} newTab
                                     testid={`admin-venues-open-${v.venue_id}`}>Open venue page</ActionItem>
                        <ActionItem to={`/admin/inventory/workspaces?venue_id=${v.venue_id}`}
                                     testid={`admin-venues-view-workspaces-${v.venue_id}`}>
                          View {v.workspace_count} workspace(s)
                        </ActionItem>
                        <ActionItem to={`/admin/venues/${v.venue_id}/edit`}
                                     testid={`admin-venues-edit-${v.venue_id}`}>
                          Edit venue
                        </ActionItem>
                        <ActionItem to={`/admin/venues/${v.venue_id}/team`}
                                     testid={`admin-venues-team-${v.venue_id}`}>
                          Team / Access
                        </ActionItem>
                        <hr style={{ margin: "6px 0", border: 0,
                                      borderTop: "1px solid rgba(27,35,64,0.08)" }} />
                        {v.published ? (
                          <ActionItem onClick={() => { setOpenRow(null); action(v, "unpublish"); }}
                                       testid={`admin-venues-unpublish-${v.venue_id}`}>
                            Unpublish
                          </ActionItem>
                        ) : (
                          <ActionItem onClick={() => { setOpenRow(null); action(v, "publish"); }}
                                       testid={`admin-venues-publish-${v.venue_id}`}>
                            Publish
                          </ActionItem>
                        )}
                        <ActionItem onClick={() => { setOpenRow(null); action(v, "retry-geocode"); }}
                                     testid={`admin-venues-retry-geocode-${v.venue_id}`}>
                          Retry geocoding
                        </ActionItem>
                        <hr style={{ margin: "6px 0", border: 0,
                                      borderTop: "1px solid rgba(27,35,64,0.08)" }} />
                        <ActionItem to={`/spaces/${v.slug}`} newTab
                                     testid={`admin-venues-pricing-${v.venue_id}`}>
                          Manage pricing
                        </ActionItem>
                        <ActionItem to="/owner/dashboard" newTab
                                     testid={`admin-venues-availability-${v.venue_id}`}>
                          Manage availability
                        </ActionItem>
                        <ActionItem to="/owner/dashboard" newTab
                                     testid={`admin-venues-bookings-${v.venue_id}`}>
                          View bookings
                        </ActionItem>
                        <ActionItem to={`/admin`} newTab
                                     testid={`admin-venues-stripe-${v.venue_id}`}>
                          Stripe connection
                        </ActionItem>
                        <hr style={{ margin: "6px 0", border: 0,
                                      borderTop: "1px solid rgba(27,35,64,0.08)" }} />
                        <ActionItem danger onClick={() => { setOpenRow(null); action(v, "archive"); }}
                                     testid={`admin-venues-archive-${v.venue_id}`}>
                          Archive venue
                        </ActionItem>
                        <ActionItem danger onClick={() => { setOpenRow(null); action(v, "delete"); }}
                                     testid={`admin-venues-delete-${v.venue_id}`}>
                          Delete venue…
                        </ActionItem>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={{ marginTop: 24, fontSize: 12, color: "rgba(27,35,64,0.55)",
                     lineHeight: 1.7 }}>
          <strong>Location belongs to the parent venue.</strong> Workspaces inherit
          city, state, ZIP, country, latitude, longitude, timezone, and search text
          from their parent — updating a venue&apos;s address propagates to every child
          workspace automatically.
        </p>
      </div>
    </AppShell>
  );
}


function ActionItem({ children, to, onClick, disabled, danger, newTab, testid }) {
  const style = {
    display: "block", padding: "8px 14px", fontSize: 13,
    color: disabled ? "rgba(27,35,64,0.35)" : danger ? "#B03030" : "var(--m-navy)",
    cursor: disabled ? "default" : "pointer",
    background: "transparent", border: 0, width: "100%", textAlign: "left",
    textDecoration: "none",
  };
  const onEnter = e => { if (!disabled) e.currentTarget.style.background = "rgba(27,35,64,0.05)"; };
  const onLeave = e => { e.currentTarget.style.background = "transparent"; };
  if (disabled) {
    return <div style={style} data-testid={testid}>{children}</div>;
  }
  if (to) {
    return (
      <Link to={to} target={newTab ? "_blank" : undefined}
             rel={newTab ? "noreferrer" : undefined}
             style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}
             data-testid={testid}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} style={style}
             onMouseEnter={onEnter} onMouseLeave={onLeave} data-testid={testid}>
      {children}
    </button>
  );
}


// Session 42 · Minimal create-venue modal. Only `name` is required — every
// other field is optional and can be filled in on the Edit page after create.
function CreateVenueModal({ venue, setVenue, orgs, saving, onClose, onCreate }) {
  const set = (k, v) => setVenue(prev => ({ ...prev, [k]: v }));
  return (
    <div role="dialog" aria-modal="true" data-testid="admin-venue-create-modal"
         onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(15,20,40,0.55)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: "#faf8f3", borderRadius: 14, padding: "28px 32px",
                    width: "min(560px, 92vw)", maxHeight: "92vh", overflow: "auto",
                    boxShadow: "0 22px 56px rgba(0,0,0,0.28)" }}>
        <div style={{ letterSpacing: "0.18em", fontSize: 11,
                       textTransform: "uppercase", color: "rgba(27,35,64,0.55)",
                       marginBottom: 4 }}>
          Admin · Inventory
        </div>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 26, margin: "0 0 4px",
                     color: "var(--m-navy)" }}>Create new venue</h2>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "rgba(27,35,64,0.65)",
                    lineHeight: 1.5 }}>
          Only the name is required — you can add photos, amenities, and child
          workspaces on the Edit page after creation.
        </p>

        <label className="m-field" style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                          color: "rgba(27,35,64,0.55)" }}>Venue name *</span>
          <input value={venue.name}
                  onChange={e => set("name", e.target.value)}
                  placeholder="e.g., Kapow Buddy"
                  data-testid="admin-venue-create-name"
                  style={{ padding: "10px 12px", width: "100%", boxSizing: "border-box" }} />
        </label>

        <label className="m-field" style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                          color: "rgba(27,35,64,0.55)" }}>Organization (optional)</span>
          <select value={venue.organization_id}
                  onChange={e => set("organization_id", e.target.value)}
                  data-testid="admin-venue-create-org"
                  style={{ padding: "10px 12px", width: "100%", boxSizing: "border-box" }}>
            <option value="">— auto-create org from venue name —</option>
            {orgs.map(o => (
              <option key={o.organization_id} value={o.organization_id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>

        <label className="m-field" style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                          color: "rgba(27,35,64,0.55)" }}>Street address</span>
          <input value={venue.address_line_1}
                  onChange={e => set("address_line_1", e.target.value)}
                  placeholder="123 Main St"
                  data-testid="admin-venue-create-address"
                  style={{ padding: "10px 12px", width: "100%", boxSizing: "border-box" }} />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 90px 130px",
                       gap: 10, marginBottom: 12 }}>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                            color: "rgba(27,35,64,0.55)" }}>City</span>
            <input value={venue.city}
                    onChange={e => set("city", e.target.value)}
                    data-testid="admin-venue-create-city"
                    style={{ padding: "10px 12px", width: "100%", boxSizing: "border-box" }} />
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                            color: "rgba(27,35,64,0.55)" }}>State</span>
            <input value={venue.state}
                    onChange={e => set("state", e.target.value)}
                    placeholder="NH"
                    data-testid="admin-venue-create-state"
                    style={{ padding: "10px 12px", width: "100%", boxSizing: "border-box" }} />
          </label>
          <label className="m-field" style={{ margin: 0 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                            color: "rgba(27,35,64,0.55)" }}>ZIP</span>
            <input value={venue.postal_code}
                    onChange={e => set("postal_code", e.target.value)}
                    data-testid="admin-venue-create-zip"
                    style={{ padding: "10px 12px", width: "100%", boxSizing: "border-box" }} />
          </label>
        </div>

        <label className="m-field" style={{ display: "block", marginBottom: 20 }}>
          <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                          color: "rgba(27,35,64,0.55)" }}>Website (optional)</span>
          <input value={venue.website}
                  onChange={e => set("website", e.target.value)}
                  placeholder="https://…"
                  data-testid="admin-venue-create-website"
                  style={{ padding: "10px 12px", width: "100%", boxSizing: "border-box" }} />
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8,
                        marginBottom: 24, fontSize: 13, color: "var(--m-navy)" }}>
          <input type="checkbox"
                 checked={!!venue.published}
                 onChange={e => set("published", e.target.checked)}
                 data-testid="admin-venue-create-published" />
          Publish immediately (uncheck to keep as draft)
        </label>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose}
                  disabled={saving}
                  data-testid="admin-venue-create-cancel"
                  className="m-btn-ghost"
                  style={{ padding: "10px 18px" }}>
            Cancel
          </button>
          <button type="button" onClick={onCreate}
                  disabled={saving || !venue.name?.trim()}
                  data-testid="admin-venue-create-submit"
                  style={{ padding: "10px 20px", background: "var(--gold)",
                           color: "#1B2340", border: "1px solid var(--gold)",
                           borderRadius: 8, cursor: saving ? "wait" : "pointer",
                           fontWeight: 600 }}>
            {saving ? "Creating…" : "Create venue"}
          </button>
        </div>
      </div>
    </div>
  );
}

